import React, { useEffect, useId, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

const messages = defineMessages({
	showMore: {
		id: 'inline-comments-common.collapsible-comment-content.show-more',
		defaultMessage: 'Show more',
		description: 'Expand a long inline comment to read all its content.',
	},
	showLess: {
		id: 'inline-comments-common.collapsible-comment-content.show-less',
		defaultMessage: 'Show less',
		description: 'Collapse a long inline comment.',
	},
});

const styles = cssMap({
	collapsed: {
		maxHeight: 'min(320px, 50vh)',
		overflow: 'hidden',
		// Fade into the comment's background, including selected and dark surfaces.
		maskImage: 'linear-gradient(to bottom, black calc(100% - 4rem), transparent)',
	},
	content: {
		display: 'flow-root',
	},
});

export const CollapsibleCommentContent = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const contentId = useId();
	const contentRef = useRef<HTMLDivElement>(null);
	const toggleRef = useRef<HTMLButtonElement>(null);
	const [overflows, setOverflows] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const collapsed = overflows && !expanded;

	useEffect(() => {
		const content = contentRef.current;
		if (!content) return;
		const measure = () =>
			setOverflows(content.scrollHeight > Math.min(320, window.innerHeight / 2));
		let resizeFrame: number | undefined;
		const scheduleMeasure = () => {
			if (resizeFrame !== undefined) return;
			resizeFrame = window.requestAnimationFrame(() => {
				resizeFrame = undefined;
				measure();
			});
		};
		measure();
		const observer =
			typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
		observer?.observe(content);
		window.addEventListener('resize', scheduleMeasure);
		return () => {
			observer?.disconnect();
			window.removeEventListener('resize', scheduleMeasure);
			if (resizeFrame !== undefined) window.cancelAnimationFrame(resizeFrame);
		};
	}, []);

	useEffect(() => {
		if (collapsed && document.activeElement === toggleRef.current) {
			toggleRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [collapsed]);

	return (
		<Stack space="space.100">
			<Box
				id={contentId}
				xcss={collapsed ? styles.collapsed : undefined}
				onFocusCapture={() => {
					if (collapsed) setExpanded(true);
				}}
			>
				<Box ref={contentRef} xcss={styles.content}>
					{children}
				</Box>
			</Box>
			{overflows && (
				<Box>
					<Button
						ref={toggleRef}
						appearance="subtle"
						iconAfter={expanded ? ChevronUpIcon : ChevronDownIcon}
						aria-expanded={expanded}
						aria-controls={contentId}
						onClick={() => setExpanded(!expanded)}
					>
						{formatMessage(expanded ? messages.showLess : messages.showMore)}
					</Button>
				</Box>
			)}
		</Stack>
	);
};
